import React from 'react'
import { motion } from 'framer-motion'
import clsx from 'clsx'
import type { Session } from '../types'

export interface Badge {
  id: string
  title: string
  description: string
  icon: string
  unlocked: boolean
  unlockedAt?: string
}

export function computeBadges(sessions: Session[], streak: number): Badge[] {
  const totalSessions = sessions.length
  const scores = sessions.map((s) => s.overallScore)
  const perfectScores = scores.filter((s) => s >= 90).length
  const maxScore = scores.length > 0 ? Math.max(...scores) : 0

  // Count unique surahs practiced
  const uniqueSurahs = new Set(sessions.map((s) => s.surah)).size

  return [
    {
      id: 'first_recitation',
      title: 'First Step',
      description: 'Complete your first recitation',
      icon: '🎙️',
      unlocked: totalSessions >= 1,
    },
    {
      id: 'five_sessions',
      title: 'Getting Started',
      description: 'Complete 5 recitations',
      icon: '📖',
      unlocked: totalSessions >= 5,
    },
    {
      id: 'twenty_five_sessions',
      title: 'Dedicated Learner',
      description: 'Complete 25 recitations',
      icon: '🌟',
      unlocked: totalSessions >= 25,
    },
    {
      id: 'hundred_sessions',
      title: 'Centurion',
      description: 'Complete 100 recitations',
      icon: '💯',
      unlocked: totalSessions >= 100,
    },
    {
      id: 'perfect_score',
      title: 'Perfect Recitation',
      description: 'Score 90 or above on any ayah',
      icon: '🏆',
      unlocked: maxScore >= 90,
    },
    {
      id: 'five_perfects',
      title: 'Consistent Excellence',
      description: 'Score 90+ on 5 different recitations',
      icon: '⭐',
      unlocked: perfectScores >= 5,
    },
    {
      id: 'three_day_streak',
      title: 'Three Day Streak',
      description: 'Practice 3 days in a row',
      icon: '🔥',
      unlocked: streak >= 3,
    },
    {
      id: 'seven_day_streak',
      title: 'Week Warrior',
      description: 'Practice 7 days in a row',
      icon: '💪',
      unlocked: streak >= 7,
    },
    {
      id: 'thirty_day_streak',
      title: 'Monthly Master',
      description: 'Practice 30 days in a row',
      icon: '🏅',
      unlocked: streak >= 30,
    },
    {
      id: 'explorer',
      title: 'Explorer',
      description: 'Practice ayahs from 5 different surahs',
      icon: '🧭',
      unlocked: uniqueSurahs >= 5,
    },
    {
      id: 'surah_explorer',
      title: 'Surah Scholar',
      description: 'Practice ayahs from 10 different surahs',
      icon: '📚',
      unlocked: uniqueSurahs >= 10,
    },
    {
      id: 'night_owl',
      title: 'Night Reciter',
      description: 'Practice after midnight',
      icon: '🌙',
      unlocked: sessions.some((s) => {
        const hour = new Date(s.createdAt).getHours()
        return hour >= 0 && hour < 5
      }),
    },
  ]
}

const BadgeCard: React.FC<{ badge: Badge }> = ({ badge }) => (
  <motion.div
    whileHover={badge.unlocked ? { scale: 1.05 } : undefined}
    className={clsx(
      'flex flex-col items-center p-4 rounded-xl border text-center transition-all',
      badge.unlocked
        ? 'border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/5'
        : 'border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/30 opacity-50 grayscale'
    )}
  >
    <span className="text-3xl mb-2" role="img" aria-label={badge.title}>
      {badge.icon}
    </span>
    <h4 className={clsx(
      'text-sm font-semibold mb-0.5',
      badge.unlocked ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-500'
    )}>
      {badge.title}
    </h4>
    <p className="text-xs text-gray-500 dark:text-gray-400">{badge.description}</p>
    {badge.unlocked && (
      <span className="mt-2 text-xs text-emerald-600 dark:text-emerald-400 font-medium">Unlocked</span>
    )}
  </motion.div>
)

const AchievementsGrid: React.FC<{ sessions: Session[]; streak: number }> = ({ sessions, streak }) => {
  const badges = computeBadges(sessions, streak)
  const unlocked = badges.filter((b) => b.unlocked).length

  return (
    <div className="glass-card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          Achievements
        </h3>
        <span className="text-xs text-emerald-500 font-medium">
          {unlocked}/{badges.length} unlocked
        </span>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        {badges.map((badge) => (
          <BadgeCard key={badge.id} badge={badge} />
        ))}
      </div>
    </div>
  )
}

export default AchievementsGrid
